import {
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  Input,
  InputLabel,
  Paper,
  Stack,
  TextField,
  TextareaAutosize,
} from "@mui/material";
import React, { useState } from "react";
import NavBar from "../../components/navbar";
import { useNavigate } from "react-router-dom";
import { useErrorHandler } from "../../hooks";
import { IMaskInput } from "react-imask";
import { LoadPositionList } from "../../../../domain/usecases/load-position-list";
import { LoadProductListByManyCodes } from "../../../../domain/usecases/load-product-list-by-many-codes";
import { Position } from "../../../../domain/models/position-model";
import { Inventory } from "../../../../domain/models/inventory";
import { UpdateInventoryAmount } from "../../../../domain/usecases/update-inventory-amount";

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const TextMaskCustom = React.forwardRef<HTMLElement, CustomProps>(
  function TextMaskCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
      <IMaskInput
        {...other}
        mask="00.00.00.00"
        inputRef={ref}
        onAccept={(value: any) =>
          onChange({ target: { name: props.name, value } })
        }
        overwrite
      />
    );
  }
);

type Props = {
  loadPosition: LoadPositionList;
  loadProductList: LoadProductListByManyCodes;
  updateInventory: UpdateInventoryAmount;
};

const AdjustInventory = ({
  loadPosition,
  loadProductList,
  updateInventory,
}: Props) => {
  const [positions, setPositions] = useState<Position[]>([]);
  const [newAmount, setNewAmount] = useState("");
  const [reason, setReason] = useState("");
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentInventory, setCurrentInventory] = useState<Inventory | null>(
    null
  );
  const navigate = useNavigate();

  const handleError = useErrorHandler((e) => {
    alert(e.message);
  });

  const handleClick = async () => {
    const warehouseString = localStorage.getItem("currentWarehouse");
    const warehouse = warehouseString
      ? (JSON.parse(warehouseString) as {
          id: number;
          name: string;
        } | null)
      : undefined;
    const [street, building, level, apartment] = code.split(".");
    const params: any = {
      warehouseId: warehouse!.id,
      filterOptions: {
        includeInventory: true,
      },
    };
    if (street) {
      params.filterOptions.street = Number(street);
    }
    if (building) {
      params.filterOptions.building = Number(building);
    }
    if (level) {
      params.filterOptions.level = Number(level);
    }
    if (apartment) {
      params.filterOptions.apartment = Number(apartment);
    }
    setIsLoading(true);
    try {
      const res = await loadPosition.load(params);

      if (res) {
        const codes: string[] = [];
        res.forEach((position) => {
          position.inventories?.forEach((inventory) => {
            codes.push(inventory!.item!.code);
          });
        });
        if (codes.length === 0) return;
        const products = await loadProductList.load(codes);
        res.forEach((position) => {
          position.inventories?.forEach((inventory) => {
            const match = products.find(
              (product) => product.eanCode === inventory.item?.code
            );
            inventory.item!.description = match?.description;
            inventory.item!.packageAmount = match?.packageAmount;
            inventory.item!.reference = match?.reference;
            inventory.item!.unit = match?.unit!;
          });
        });
      }
      setPositions(res);
    } catch (error) {
      handleError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateInventory = async (id: number) => {
    if (!newAmount) return alert("Insira um nova quantidade");
    if (!reason) return alert("Insira um motivo.");
    setIsLoading(true);
    try {
      await updateInventory.update({
        id: currentInventory!.id!,
        amount: Number(
          (
            Number(newAmount) * (currentInventory?.item?.packageAmount || 1)
          ).toFixed(4)
        ),
        reason: reason,
      });
      setReason("");
      setNewAmount("");
      setCurrentInventory(null);
      handleClick();
    } catch (error: any) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <NavBar
        title="Ajustar Estoque"
        onBack={() => {
          navigate("/adjustments");
        }}
      />
      <Dialog
        open={!!currentInventory}
        onClose={() => {
          setCurrentInventory(null);
        }}
      >
        <DialogTitle>Alterar quantidade do produto?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Atenção! Essa ação não é reversível e pode afetar a integridade do
            estoque.
          </DialogContentText>
          {currentInventory && (
            <Stack spacing={2} my={2}>
              <Stack>
                <div style={{ fontWeight: "bold" }}>Descrição</div>
                <div>{currentInventory?.item?.description}</div>
              </Stack>
              <Stack>
                <div style={{ fontWeight: "bold" }}>Lote</div>
                <div>{currentInventory?.item?.batch}</div>
              </Stack>
              <Stack>
                <div style={{ fontWeight: "bold" }}>Quantidade</div>
                <div>
                  {(
                    currentInventory.amount /
                    (currentInventory?.item?.packageAmount || 1)
                  ).toFixed(2)}
                </div>
              </Stack>
            </Stack>
          )}
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Nova quantidade"
            fullWidth
            variant="outlined"
            value={newAmount}
            onChange={(e) => {
              const value = e.target.value;
              if (/^[0-9]+$/.test(value) || value === "") {
                setNewAmount(value);
              }
            }}
          />
          <Stack spacing={2} my={2}>
            <label>Motivo</label>
            <TextareaAutosize
              autoFocus
              id="name"
              value={reason}
              style={{ minHeight: "40px" }}
              onChange={(e) => {
                setReason(e.target.value);
              }}
            />
          </Stack>
        </DialogContent>
        {isLoading ? (
          <DialogActions
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div>
              <CircularProgress />
            </div>
          </DialogActions>
        ) : (
          <DialogActions>
            <Button
              onClick={() => {
                setCurrentInventory(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (currentInventory?.id) {
                  handleUpdateInventory(Number(currentInventory.id));
                }
              }}
            >
              Confirmar
            </Button>
          </DialogActions>
        )}
      </Dialog>
      <Container
        sx={{
          my: "50px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack direction="row" spacing={4}>
          <FormControl>
            <InputLabel htmlFor="formatted-text-mask-input">Posição</InputLabel>
            <Input
              value={code}
              onChange={(e: any) => {
                setCode(e.target.value);
              }}
              name="textmask"
              id="formatted-text-mask-input"
              inputComponent={TextMaskCustom as any}
            />
          </FormControl>
        </Stack>
      </Container>
      <Divider>
        <Button
          variant="contained"
          size="large"
          startIcon={
            isLoading && <CircularProgress color="secondary" size={20} />
          }
          onClick={handleClick}
        >
          Pesquisar
        </Button>
      </Divider>
      <Container sx={{ my: "50px" }}>
        {positions.map((position) => {
          return position.inventories?.map((inventory) => {
            return (
              <Paper
                key={inventory.id}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  padding: "20px",
                  marginTop: "30px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "space-between",
                    width: "100%",
                  }}
                >
                  <Stack>
                    <div style={{ fontWeight: "bold" }}>Código EAN</div>
                    <div>{inventory.item?.code}</div>
                  </Stack>
                  <Stack>
                    <div style={{ fontWeight: "bold" }}>Descrição</div>
                    <div>{inventory.item?.description}</div>
                  </Stack>
                  <Stack>
                    <div style={{ fontWeight: "bold" }}>Lote</div>
                    <div>{inventory.item?.batch}</div>
                  </Stack>
                  <Stack>
                    <div style={{ fontWeight: "bold" }}>Referência</div>
                    <div>{inventory.item?.reference}</div>
                  </Stack>
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: "20px",
                  }}
                >
                  <Stack direction="row" spacing={3}>
                    <div style={{ fontWeight: "bold" }}>Quantidade</div>
                    <div>{`${(
                      inventory.amount / (inventory.item?.packageAmount || 1)
                    ).toFixed(2)} (${inventory.amount} ${
                      inventory.item?.unit
                    })`}</div>
                  </Stack>
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: "20px",
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={() => {
                      setCurrentInventory(inventory || null);
                    }}
                  >
                    Alterar Quantidade
                  </Button>
                </div>
              </Paper>
            );
          });
        })}
      </Container>
    </>
  );
};

export default AdjustInventory;
